// /login/verify — enter the 6-digit code (factor 2). Auto-verifies when complete.
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthLayout from '../components/AuthLayout.jsx';
import OtpInput from '../components/OtpInput.jsx';
import { takeReturn } from '../lib/returnTo.js';
import { verifyOtp } from '../api/auth.js';
import { useAuthStore } from '../stores/authStore.js';

export default function VerifyOtp() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;
  const setUser = useAuthStore((s) => s.setUser);

  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!email) navigate('/login', { replace: true });
  }, [email, navigate]);

  const doVerify = async (fullCode) => {
    setError('');
    setLoading(true);
    try {
      const { user } = await verifyOtp(email, fullCode);
      setUser(user);
      navigate(takeReturn() || '/', { replace: true });
    } catch (err) {
      const data = err.response?.data?.error;
      const remaining = data?.details?.remaining;
      const suffix = Number.isInteger(remaining)
        ? ` ${remaining} attempt${remaining === 1 ? '' : 's'} left.`
        : '';
      setError((data?.message || 'Verification failed.') + suffix);
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  // A new code can only be issued by the password step, so this returns to sign-in (with the email
  // pre-filled) and says so — the button used to promise "Get a new code" and then silently send you
  // back to a form, which reads as broken.
  const resend = () =>
    navigate('/login', { state: { email, notice: 'Enter your password again and we\'ll send you a new code.' } });

  if (!email) return null;

  return (
    <AuthLayout>
      <h1 className="text-lg font-semibold text-text-primary">Enter your code</h1>
      <p className="mt-1 text-sm text-text-secondary">
        We sent a 6-digit code to <span className="font-medium text-text-primary">{email}</span>.
      </p>

      <div className="mt-5">
        <OtpInput value={code} onChange={setCode} onComplete={doVerify} error={Boolean(error)} disabled={loading} />
      </div>

      {error && <p className="mt-3 text-sm text-error">{error}</p>}

      {import.meta.env.DEV && (
        <p className="mt-3 rounded-md bg-warning-light px-3 py-2 text-xs text-warning">
          Dev mode: your code is printed in the terminal running{' '}
          <span className="font-mono">npm run dev</span>.
        </p>
      )}

      <div className="mt-6 flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={() => navigate('/login')}
          className="text-text-secondary transition hover:text-text-primary"
        >
          ← Back to sign in
        </button>
        <button
          type="button"
          onClick={resend}
          className="font-medium text-accent transition hover:text-accent-hover"
        >
          Send a new code
        </button>
      </div>
    </AuthLayout>
  );
}
